import React, { useState } from "react";
import ReactApexChart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import styled from "styled-components";
import LineBar from "./LineBar";
import { MetricsRecord } from "./ActivityChart";
import { IoMdClose } from "react-icons/io";

// Icons
import stepsIcon from "../assets/footprint-fill.png";
import distanceIcon from "../assets/pin-distance-fill.png";
import CaloriesIcon from "../assets/fire-line.png";
import stepsBackground from "../assets/LineBarBackground.png";
import distanceBackground from "../assets/LineBarBackground2.png";
import CaloriesBackground from "../assets/LineBarBackground3.png";

// FIX: Import the General Goals Image
import GoalsPopupImage from "../assets/Goals.png";

interface GoalsOverviewProps {
  goalSource: "self" | "org";
  orgMetrics: MetricsRecord[];
  selfMetrics: MetricsRecord[];
  onGoalToggle: (val: "self" | "org") => void;
}

const GoalsOverview: React.FC<GoalsOverviewProps> = ({
  goalSource,
  orgMetrics,
  selfMetrics,
  onGoalToggle,
}) => {
  const [currentMetric, setCurrentMetric] = useState("Steps");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [popupImageModal, setPopupImageModal] = useState<string | null>(null);
  const chartLabels = ["Steps", "Distance", "Calories"];

  const data = goalSource === "org" ? orgMetrics : selfMetrics;

  // --- HANDLER: Just Selects Metric (No Popup) ---
  const handleSelection = (metric: string, index: number) => {
    setCurrentMetric(metric);
    setSelectedIndex(index);
  };

  const chartOptions: ApexOptions = {
    chart: {
      type: "radialBar",
      animations: { enabled: true, speed: 800 },
      events: {
        // Clicking a slice selects it, but we stop propagation to let the 
        // parent ChartSection onClick handle the popup if desired, 
        // OR we can just let it bubble up. 
        // Here we just handle selection logic.
        dataPointSelection: (_event, _chartContext, config) => {
          const index = config.dataPointIndex;
          const label = chartLabels[index];
          handleSelection(label, index);
        },
      },
    },
    plotOptions: {
      radialBar: {
        hollow: { size: "55%" },
        track: { background: "#F1F5F9" },
        dataLabels: {
          name: { show: true, fontSize: "13px", color: "#888", offsetY: -10 },
          value: {
            show: true,
            fontSize: "24px",
            fontWeight: "800",
            color: "var(--text-color, #0F172A)",
            offsetY: 5,
          },
          total: {
            show: true,
            label: chartLabels[selectedIndex],
            color: "var(--text-color, #0F172A)",
            formatter: () => `${Math.round(chartSeries[selectedIndex])}%`,
          },
        },
      },
    },
    colors: ["#094752", "#1D9DA2", "#5BDFDA"],
    labels: chartLabels,
    stroke: { lineCap: "round" },
  };

  const getChartSeries = (data: MetricsRecord[]): number[] => {
    if (!data.length || !data[0]?.metrics?.[0]) return [0, 0, 0];
    const { steps, distance, caloriesburned } = data[0].metrics[0] as any;
    return [
      steps?.performance || 0,
      distance?.performance || 0,
      caloriesburned?.performance || 0,
    ];
  };

  const chartSeries = getChartSeries(data);

  return (
    <UnifiedCard>
      <CardHeader>
        <TitleSection>
          <h3>Goals Overview</h3>
          <span className="subtitle">Track your daily progress targets</span>
        </TitleSection>

        <ToggleWrapper>
          <ToggleButton
            active={goalSource === "org"}
            onClick={() => onGoalToggle("org")}
          >
            Org
          </ToggleButton>
          <ToggleButton
            active={goalSource === "self"}
            onClick={() => onGoalToggle("self")}
          >
            Self
          </ToggleButton>
        </ToggleWrapper>
      </CardHeader>

      <CardBody>
        {/* Left: Metrics List */}
        <MetricsList>
          <LineBar
            achieved={data[0]?.metrics?.[0]?.steps?.value ?? 0}
            total={data[0]?.metrics?.[0]?.steps?.target ?? 0}
            metric="Steps"
            isActive={currentMetric === "Steps"}
            // Click selects only (No Popup)
            onSelect={() => handleSelection("Steps", 0)}
            icon={stepsIcon}
            backgroundImage={stepsBackground}
            animationDelay={0.1}
            data={data}
          />
          <LineBar
            achieved={data[0]?.metrics?.[0]?.distance?.value ?? 0}
            total={data[0]?.metrics?.[0]?.distance?.target ?? 0}
            metric="Distance"
            isActive={currentMetric === "Distance"}
            // Click selects only (No Popup)
            onSelect={() => handleSelection("Distance", 1)}
            icon={distanceIcon}
            backgroundImage={distanceBackground}
            animationDelay={0.2}
            data={data}
          />
          <LineBar
            achieved={data[0]?.metrics?.[0]?.caloriesburned?.value ?? 0}
            total={data[0]?.metrics?.[0]?.caloriesburned?.target ?? 0}
            metric="Calories"
            isActive={currentMetric === "Calories"}
            // Click selects only (No Popup)
            onSelect={() => handleSelection("Calories", 2)}
            icon={CaloriesIcon}
            backgroundImage={CaloriesBackground}
            animationDelay={0.3}
            data={data}
          />
        </MetricsList>

        {/* Right: Chart Section (The General Click Trigger) */}
        <ChartSection
          onClick={() => setPopupImageModal(GoalsPopupImage)}
          title="Click to view details"
        >
          <ReactApexChart
            options={chartOptions}
            series={chartSeries}
            type="radialBar"
            height={280}
            width={"100%"}
          />
        </ChartSection>
      </CardBody>

      {/* Modal */}
      {popupImageModal && (
        <ModalOverlay onClick={() => setPopupImageModal(null)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <CloseIcon onClick={() => setPopupImageModal(null)} />
            <img src={popupImageModal} alt="Metric Details" />
          </ModalContent>
        </ModalOverlay>
      )}
    </UnifiedCard>
  );
};

export default GoalsOverview;

// --- STYLED COMPONENTS ---

const UnifiedCard = styled.div`
  background: var(--card-background, #ffffff);
  border-radius: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  padding: 16px;
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  box-sizing: border-box;
  overflow: visible;

  @media (min-width: 768px) {
    padding: 24px;
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
`;

const TitleSection = styled.div`
  h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    color: var(--text-color, #1E293B);
  }
  .subtitle {
    font-size: 12px;
    color: #94A3B8;
    margin-top: 2px;
    display: block;
  }
  @media (min-width: 768px) {
    h3 { font-size: 18px; }
    .subtitle { font-size: 13px; }
  }
`;

const CardBody = styled.div`
  display: flex;
  flex-direction: row; 
  align-items: center;
  gap: 10px;
  flex: 1;
`;

const MetricsList = styled.div`
  flex: 1.2;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

const ChartSection = styled.div`
  flex: 0.8; 
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: 0;
  cursor: pointer;
  transition: transform 0.2s;

  &:hover {
    transform: scale(0.82); 
  }
  
  transform: scale(0.8); 
  transform-origin: center center;
  margin-right: -15px; 
  
  @media (min-width: 768px) {
    transform: none;
    flex: 1;
    margin-right: 0;
    &:hover { transform: scale(1.02); }
  }
`;

const ToggleWrapper = styled.div`
  display: flex;
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 12px;
  padding: 2px;
`;

const ToggleButton = styled.button<{ active: boolean }>`
  padding: 4px 10px;
  background: ${(props) => (props.active ? "white" : "transparent")};
  color: ${(props) => (props.active ? "#0F172A" : "#64748B")};
  box-shadow: ${(props) => (props.active ? "0 2px 4px rgba(0,0,0,0.05)" : "none")};
  border: none;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    color: #0F172A;
  }
`;

const ModalOverlay = styled.div`
  position: fixed; top: 0; left: 0; width: 100vw; height: 100vh;
  background: rgba(0, 0, 0, 0.6); display: flex;
  justify-content: center; align-items: center; z-index: 999;
`;

// FIX: Updated ModalContent to prevent image from being too zoomed
const ModalContent = styled.div`
  background: white; 
  padding: 16px; 
  border-radius: 12px;
  position: relative; 
  
  /* Restrict max dimensions */
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  
  display: flex;
  align-items: center;
  justify-content: center;

  img { 
    width: 100%; 
    height: auto; 
    max-height: 60vh; /* Prevents vertical overflow/zooming */
    object-fit: contain;
    border-radius: 8px; 
  }
`;

const CloseIcon = styled(IoMdClose)`
  position: absolute; top: 10px; right: 10px; font-size: 24px; cursor: pointer; color: #333;
`;